import { createSelector } from '@reduxjs/toolkit';
import { RootState } from './state';
import { Subscription } from '@wix/ambassador-billing-v1-subscription/types';
import { buildLineItemResources } from './lineItemResources';
import { getCustomerSettingsAppId } from './domainUtils';

export const getCancelConfirmModal = (state: RootState) => state.cancelConfirmModal;
export const getCancelConfirmModalSubscriptionId = (state: RootState) => getCancelConfirmModal(state).subscriptionId;
export const isCancelConfirmModalOpen = (state: RootState) => getCancelConfirmModal(state).isOpen;
export const isCancelConfirmModalSubmitting = (state: RootState) => getCancelConfirmModal(state).submitting === true;
export const getCancelConfirmModalSubscription = (state: RootState) =>
  getSubscriptionById(state, getCancelConfirmModalSubscriptionId(state));
export const getSkipConfirmModal = (state: RootState) => state.skipConfirmModal;
export const getSkipConfirmModalSubscriptionId = (state: RootState) => getSkipConfirmModal(state).subscriptionId;
export const isSkipConfirmModalOpen = (state: RootState) => getSkipConfirmModal(state).isOpen;
export const getSkipConfirmModalSubscription = (state: RootState) =>
  getSubscriptionById(state, getSkipConfirmModalSubscriptionId(state));
export const getCancelScheduledSkipConfirmModal = (state: RootState) => state.cancelScheduledSkipConfirmModal;
export const getCancelScheduledSkipConfirmModalSubscriptionId = (state: RootState) =>
  getCancelScheduledSkipConfirmModal(state).subscriptionId;
export const isCancelScheduledSkipConfirmModalOpen = (state: RootState) =>
  getCancelScheduledSkipConfirmModal(state).isOpen;
export const getCancelScheduledSkipConfirmModalSubscription = (state: RootState) =>
  getSubscriptionById(state, getCancelScheduledSkipConfirmModalSubscriptionId(state));
export const isUpmModalOpen = (state: RootState) => state.upmModal.isOpen;
export const isPayNowModalOpen = (state: RootState) => state.payNowModal.isOpen;
export const getUpmModalSubscription = (state: RootState): Subscription =>
  getSubscriptionById(state, state.upmModal.subscriptionId);
export const getPayNowModalSubscription = (state: RootState): Subscription =>
  getSubscriptionById(state, state.payNowModal.subscriptionId);
export const getSubscriptions = (state: RootState) => state.subscriptions;
export const getSubscriptionById = (state: RootState, id: string | null) =>
  (getSubscriptions(state).entities as any[]).find((subscription: Subscription) => subscription.id === id);
export const isLoading = (state: RootState) => state.subscriptions.loading === 'pending';
export const getDetails = (state: RootState, subscriptionId: string) => state.details.entities[subscriptionId];
export const isDetailsOpen = (state: RootState, subscriptionId: string) => state.accordion.indexOf(subscriptionId) > -1;
export const isDetailsLoading = (state: RootState, subscriptionId: string) =>
  state.details.loading.indexOf(subscriptionId) > -1;
export const getBillingSubscriptionIdByContractId = (state: RootState, contractId: string): string | undefined =>
  state.subscriptions.entities.find(
    (subscription: Subscription) => state.details.entities[subscription.id!]?.storesSubscription?.id === contractId,
  )?.id ?? undefined;
export const getLineItemResources = createSelector(
  [(state: RootState) => state.subscriptions.entities, (state: RootState) => state.details.entities],
  (subscriptions, details) =>
    subscriptions.flatMap((subscription: Subscription) =>
      buildLineItemResources(subscription, details[subscription.id!]?.storesSubscription),
    ),
);
const getAppBillingSettings = (state: RootState, subscription?: Subscription) => {
  const appId =
    subscription && state.billingSettings.resolveStoresByPredicate
      ? getCustomerSettingsAppId(subscription)
      : subscription?.origin?.appId;
  return appId ? state.billingSettings.byAppId[appId] : undefined;
};
export const isSkipEnabled = (state: RootState, subscription?: Subscription) =>
  getAppBillingSettings(state, subscription)?.skipEnabled === true;
export const getMinStartNoticeDays = (state: RootState, subscription?: Subscription) =>
  getAppBillingSettings(state, subscription)?.skipConfig?.minStartNoticeDays ?? 0;
export const getMinRevocationNoticeDays = (state: RootState, subscription?: Subscription) =>
  getAppBillingSettings(state, subscription)?.skipConfig?.minRevocationNoticeDays ?? 0;
export const getMinEarlyEndNoticeDays = (state: RootState, subscription?: Subscription) =>
  getAppBillingSettings(state, subscription)?.skipConfig?.minEarlyEndNoticeDays ?? 0;
export const getMaxUserActions = (state: RootState, subscription?: Subscription) =>
  getAppBillingSettings(state, subscription)?.skipConfig?.maxUserActions ?? 0;
export const isCancelEnabled = (state: RootState, subscription?: Subscription) =>
  getAppBillingSettings(state, subscription)?.cancelEnabled !== false;
export const getLanguage = (state: RootState) => state.language;
export const getRegionalSettings = (state: RootState) => state.regionalSettings;
export const isPricingPlansInstalled = (state: RootState) => state.pricingPlansInstalled;
//* * dont delete until petri support correctly filter by language  */
// export const isFeatureEnabled = (state: RootState, experiment: string) =>
//   state.experiments[experiment] === 'true' || state.experiments[experiment] === true;
//* * end of non delete comment  */
